import { useNavigate, useParams } from 'react-router-dom';
import { AddThemeRequest, useAddThemeMutation } from 'src/api/themes';
import { useGlobalModals } from 'src/contexts/global-modals';
import { validateEmpty } from 'src/helpers/validators';
import { links } from 'src/router/routes';

export type FormData = {
  name: string;
  theme_id: number;
  image: File | null;
};

const validate = {
  name: (value: string) => validateEmpty(value, 'Name is required'),
  theme_id: (value: number) =>
    !value || value <= 0 ? 'Theme ID is required' : null,
  image: (value: File | null) => (!value ? 'Image is required' : null),
};

const initialValues: FormData = {
  name: '',
  theme_id: 1,
  image: null,
};

export const useAddTheme = () => {
  const navigate = useNavigate();
  const { dictionaryId } = useParams<{ dictionaryId: string }>();
  const { addThemeMutation, isPending } = useAddThemeMutation();
  const { openLoadingModal, closeLoadingModal } = useGlobalModals();

  const handleSubmit = async (values: FormData) => {
    if (!values.image || !dictionaryId) {
      return;
    }

    const data: AddThemeRequest = {
      name: values.name,
      theme_id: values.theme_id,
      image: values.image,
      interaction_dictionary_id: parseInt(dictionaryId),
    };

    openLoadingModal('Adding theme', 'Please wait while we add the theme...');

    try {
      const result = await addThemeMutation(data);

      if (result) {
        navigate(`${links.interactionDictionary}/${dictionaryId}`);
      }
    } catch (error) {
      console.error('Failed to add theme:', error);
    } finally {
      closeLoadingModal();
    }
  };

  const handleCancel = () => {
    navigate(links.interactionsDictionary);
  };

  return {
    handleSubmit,
    handleCancel,
    initialValues,
    isPending,
    validate,
  };
};
